
import { useRef, useEffect, useState, useCallback } from 'react'
import { useThree, useFrame, createPortal } from '@react-three/fiber'
import * as THREE from 'three'
import { CameraControls as CameraControlsComponent, BakeShadows } from '@react-three/drei'
import {
  DEFAULT_CAMERA_POSITIONS,
  BLACK_ARROW_POI_LIMITS,
  LAUNCH_PLATFORM_POI_LIMITS,
  LAUNCH_PLATFORM_CAMERA_LIMITS,
  CAMERA_CONTROLS_DEFAULTS,
  ANIMATION_TIMINGS,
  getDefaultCameraPosition
} from './camera/CameraConfig.js'
import { CameraMovementController } from './camera/CameraController.jsx'

/**
 * Get the appropriate camera target for scene 2 based on current orientation
 * @returns {THREE.Vector3} Target position with orientation offset applied
 */
function getScene2TargetWithOffset() {
  const isPortrait = window.innerWidth < window.innerHeight
  const isLandscape = !isPortrait
  const target = getDefaultCameraPosition('scene2', isLandscape).target.clone()
  target.x = isPortrait ? 2 : 0

  return target
}

/**
 * TripleSceneManager - Manages three synchronized scenes rendering to separate textures
 * Scene 1: Launch platform with environment (exploded view)
 * Scene 2: Rocket with all parts assembled  
 * Scene 3: Prospero only with Earth environment
 */
export default function TripleSceneManager({ 
  scene1Content, 
  scene2Content,
  scene3Content,
  onScene1Texture,
  onScene2Texture,
  onScene3Texture,
  renderScene1 = true,
  renderScene2 = false,
  renderScene3 = false,
  mountScene1 = true,
  onScenesReady,
  hdriBlend = 0,
  scene3Blend = 0,
  isAnimating = false,
  isAtPOI = false,
  activePOI = null,
  cameraZoom = 2000,
  shouldBakeShadows = false,
  cameraRotation = 0
}) {
  const { gl, size, domElement, camera: canvasCamera } = useThree()

  // Store the initial camera state once
  const initialCameraState = useRef(null)
  if (!initialCameraState.current) {
    initialCameraState.current = {
      position: canvasCamera.position.clone(),
      quaternion: new THREE.Quaternion().copy(canvasCamera.quaternion),
      zoom: canvasCamera.zoom,
      far: canvasCamera.far,
      near: canvasCamera.near
    }
  }

  // CRITICAL: Lock the orthographic canvas camera in place - it must NEVER move
  // Run BOTH early (before everything) AND late (after everything) to catch all modifications
  useFrame(() => {
    canvasCamera.position.copy(initialCameraState.current.position)
    canvasCamera.quaternion.copy(initialCameraState.current.quaternion)
    canvasCamera.zoom = initialCameraState.current.zoom
    canvasCamera.updateProjectionMatrix()
  }, -10000) // Run BEFORE everything else
  
  useFrame(() => {
    canvasCamera.position.copy(initialCameraState.current.position)
    canvasCamera.quaternion.copy(initialCameraState.current.quaternion)
    canvasCamera.zoom = initialCameraState.current.zoom
    canvasCamera.updateProjectionMatrix()
  }, 10000) // Run AFTER everything else

  // Track previous hdriBlend to detect scene transitions
  const prevHdriBlend = useRef(hdriBlend)
  const hasDispatchedReturnAnimation = useRef(false)

  // When fade transition starts going back to scene 1, trigger camera animation
  useEffect(() => {
    if (prevHdriBlend.current >= 1 && hdriBlend < 1 && !hasDispatchedReturnAnimation.current) {
      // Dispatch camera move event to animate back to scene 1 during fade
      const isPortrait = window.innerWidth < window.innerHeight
      const isLandscape = !isPortrait
      const defaultScene1 = getDefaultCameraPosition('scene1', isLandscape)
      window.dispatchEvent(new CustomEvent('scene-camera-move', {
        detail: {
          targetPosition: defaultScene1.position.clone(),
          targetLookAt: defaultScene1.target.clone(),
          duration: ANIMATION_TIMINGS.cameraMovement
        }
      }))
      hasDispatchedReturnAnimation.current = true
    }

    // Reset flag when fully back in scene 1
    if (hdriBlend < 0.1) {
      hasDispatchedReturnAnimation.current = false
    }

    prevHdriBlend.current = hdriBlend
  }, [hdriBlend])

    // When transitioning to scene 2, camera target is handled by interpolation effect below
  // No camera-move event needed - interpolation handles the smooth transition
  useEffect(() => {
    if (prevHdriBlend.current < 1 && hdriBlend >= 1) {
      // Camera position animation is handled by the camera-move event from the return-to-scene-1 effect
      // Camera target is continuously updated by the hdriBlend interpolation effect

    }
    prevHdriBlend.current = hdriBlend
  }, [hdriBlend])

  // Use POI limits from centralized config
  const poiLimits = BLACK_ARROW_POI_LIMITS
  
  // Camera FOV values (hardcoded)
  const cameraControls = {
    scene1Fov: 24,
    scene2Fov: 24,
  }
  
  // Create separate scenes
  const scene1Ref = useRef(new THREE.Scene())
  const scene2Ref = useRef(new THREE.Scene())
  const scene3Ref = useRef(new THREE.Scene())
  
  // Initialize Scene 2 with radial gradient background
  useEffect(() => {
    if (scene2Ref.current) {
      // Create a canvas texture for the radial gradient background with ultra-high resolution
      const canvas = document.createElement('canvas')
      canvas.width = 4096
      canvas.height = 4096
      const ctx = canvas.getContext('2d')
      
      // Create radial gradient with very large radius for smooth blending
      const centerX = 2048
      const centerY = 2048
      const maxRadius = 4096
      
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, maxRadius)
      gradient.addColorStop(0, '#333333')      // Very dark grey center
      gradient.addColorStop(1, '#111111')      // Black outer
      
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, 4096, 4096)
      
      // Create texture from canvas with linear color space for better interpolation
      const texture = new THREE.CanvasTexture(canvas)
      texture.colorSpace = THREE.SRGBColorSpace
      scene2Ref.current.background = texture
    }
  }, [])
  
  // Scene 3 has no manual background - uses HDRI Environment from scene3Content
  
  const camera1Ref = useRef(null)
  const camera2Ref = useRef(null)
  const camera3Ref = useRef(null)
  
  // Create render targets for all three scenes
  const renderTarget1Ref = useRef(null)
  const renderTarget2Ref = useRef(null)
  const renderTarget3Ref = useRef(null)
  
  // Ref for OrbitControls
  const controlsRef = useRef(null)
  
  // Track if scenes have been exposed to parent
  const scenesExposedRef = useRef(false)
  
  // Track if initial target has been set
  const initialTargetSetRef = useRef(false)
  
  // Camera movement state (for CameraMovementController)
  const [moveTarget, setMoveTarget] = useState(null)
  const [moveAnimate, setMoveAnimate] = useState(false)
  
  // Expose scenes and camera to parent
  useEffect(() => {
    if (onScenesReady && camera1Ref.current && controlsRef.current && !scenesExposedRef.current) {
      scenesExposedRef.current = true
      onScenesReady({
        scene1: scene1Ref,
        scene2: scene2Ref,
        camera: camera1Ref,
        controls: controlsRef.current
      })
    }
  }, [onScenesReady])

  // Expose controls to window for direct access
  useEffect(() => {
    if (controlsRef.current) {
      window.__cameraControls = controlsRef.current
    }
  }, [])

  // Wrap controls rotate method to swap pitch/yaw in landscape
  // Use useFrame to continuously check if we need to wrap
  useFrame(() => {
    if (!controlsRef.current) return

    const controls = controlsRef.current

    // Only wrap once per controls instance (using a flag on the object itself)
    if (controls.__rotateMethodWrapped) return
    
    controls.__rotateMethodWrapped = true

    if (!controls.rotate) return

    const originalRotate = controls.rotate.bind(controls)

    controls.rotate = function(deltaAzimuth, deltaPolarAngle, enableTransition) {
      // Check orientation dynamically each time rotate is called
      const isLandscape = typeof window !== 'undefined' && window.innerWidth > window.innerHeight
      // Get hdriBlend from window if available (it's set by app context)
      const hdriBlend = window.__hdriBlend || 0
      const isScene2 = hdriBlend >= 0.99  // Scene 2 is when hdriBlend is essentially 1
      
      if (isLandscape && isScene2) {
        // Swap the axes and reverse left/right ONLY in Scene 2 landscape mode
        const newAz = deltaPolarAngle  // No negation - normal up/down
        const newPolar = -deltaAzimuth  // Negate to reverse left/right
        return originalRotate.call(this, newAz, newPolar, enableTransition)
      } else {
        // Normal behavior everywhere else (Scene 1, or portrait mode in Scene 2)
        return originalRotate.call(this, deltaAzimuth, deltaPolarAngle, enableTransition)
      }
    }
  })
  
  // Listen for camera movement events
  useEffect(() => {
    function handleSceneCameraMove(event) {
      if (event.detail) {
        const {
          targetPosition,
          targetLookAt,
          duration = ANIMATION_TIMINGS.cameraMovement
        } = event.detail

        if (targetPosition && targetLookAt) {
          setMoveTarget({
            position: targetPosition,
            targetLookAt,
            duration
          })
          setMoveAnimate(true)
        }
      }
    }

    function handleCameraSnap(event) {
      if (event.detail) {
        const { targetPosition, targetLookAt } = event.detail;
        setMoveTarget({
          position: targetPosition ? targetPosition.toArray() : null,
          targetLookAt: targetLookAt ? targetLookAt.toArray() : null
        });

        // First, unmount the old controller by setting moveAnimate to false
        setMoveAnimate(false);
        
        // Then immediately (in next render) mount a new controller with 1ms duration
        setTimeout(() => {
          setMoveTarget({
            position: targetPosition,
            targetLookAt,
            duration: 1
          });
          setMoveAnimate(true);
        }, 0);
      }
    }

    window.addEventListener('scene-camera-move', handleSceneCameraMove)
    window.addEventListener('scene-camera-snap', handleCameraSnap)
    return () => {
      window.removeEventListener('scene-camera-move', handleSceneCameraMove)
      window.removeEventListener('scene-camera-snap', handleCameraSnap)
    }
  }, [])
  
  useEffect(() => {
    // Create cameras only once (not on every resize)
    if (!camera1Ref.current || !camera2Ref.current || !camera3Ref.current) {
      const cam1 = new THREE.PerspectiveCamera(cameraControls.scene1Fov, size.width / size.height, 0.1, 2000)
      const cam2 = new THREE.PerspectiveCamera(cameraControls.scene2Fov, size.width / size.height, 0.1, 2000)
      const cam3 = new THREE.PerspectiveCamera(cameraControls.scene2Fov, size.width / size.height, 0.1, 2000)

      // Set initial positions from shared defaults based on orientation
      const isPortrait = size.width < size.height
      const isLandscape = !isPortrait
      
      cam1.position.copy(getDefaultCameraPosition('scene1', isLandscape).position)
      cam2.position.copy(getDefaultCameraPosition('scene2', isLandscape).position)
      
      // Scene 3 uses same camera position as scene 2
      cam3.position.copy(getDefaultCameraPosition('scene2', isLandscape).position)

      camera1Ref.current = cam1
      camera2Ref.current = cam2
      camera3Ref.current = cam3
    } else {
      // On resize: only update aspect ratio, don't recreate cameras
      camera1Ref.current.aspect = size.width / size.height
      camera1Ref.current.updateProjectionMatrix()
      
      camera2Ref.current.aspect = size.width / size.height
      camera2Ref.current.updateProjectionMatrix()
      
      camera3Ref.current.aspect = size.width / size.height
      camera3Ref.current.updateProjectionMatrix()
    }

    // Get device pixel ratio for high DPI displays (clamp to reduce performance impact)
    const pixelRatio = Math.min(window.devicePixelRatio, 1.5)
    const maxAnisotropy = gl.capabilities.getMaxAnisotropy()

    // Create optimized render targets with reduced quality for performance
    const rt1 = new THREE.WebGLRenderTarget(
      size.width * pixelRatio,
      size.height * pixelRatio,
      {
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType,
        colorSpace: THREE.LinearSRGBColorSpace,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        stencilBuffer: false, // Remove stencil buffer for performance
        samples: 4, // Reduce MSAA from 8 to 4
        anisotropy: Math.min(maxAnisotropy, 4), // Reduce anisotropy
      }
    )

    const rt2 = new THREE.WebGLRenderTarget(
      size.width * pixelRatio,
      size.height * pixelRatio,
      {
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType,
        colorSpace: THREE.LinearSRGBColorSpace,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        stencilBuffer: false, // Remove stencil buffer for performance
        samples: 4, // Reduce MSAA from 8 to 4
        anisotropy: Math.min(maxAnisotropy, 4), // Reduce anisotropy
      }
    )

    const rt3 = new THREE.WebGLRenderTarget(
      size.width * pixelRatio,
      size.height * pixelRatio,
      {
        format: THREE.RGBAFormat,
        type: THREE.HalfFloatType,
        colorSpace: THREE.LinearSRGBColorSpace,
        minFilter: THREE.LinearFilter,
        magFilter: THREE.LinearFilter,
        stencilBuffer: false,
        samples: 4,
        anisotropy: Math.min(maxAnisotropy, 4),
      }
    )

    renderTarget1Ref.current = rt1
    renderTarget2Ref.current = rt2
    renderTarget3Ref.current = rt3

    // Provide textures to parent immediately (even if not rendering yet)
    if (onScene1Texture) onScene1Texture(rt1.texture)
    if (onScene2Texture) onScene2Texture(rt2.texture)
    if (onScene3Texture) onScene3Texture(rt3.texture)

    // Render scene 2 and 3 once initially to populate the textures
    gl.setRenderTarget(rt2)
    gl.clear()
    gl.render(scene2Ref.current, camera2Ref.current)
    
    gl.setRenderTarget(rt3)
    gl.clear()
    gl.render(scene3Ref.current, camera3Ref.current)
    
    gl.setRenderTarget(null)

    return () => {
      rt1.dispose()
      rt2.dispose()
      rt3.dispose()
    }
  }, [size.width, size.height, cameraControls.scene1Fov, cameraControls.scene2Fov])
  
  // REMOVED: Interpolation effect - now handled entirely by camera-move events from UI.jsx
  // This eliminates the conflict between interpolation and camera animation
  
  // Handle orientation changes in scene 2 to update camera target offset
  useEffect(() => {
    const handleOrientationChange = () => {
      if (hdriBlend >= 1 && controlsRef.current) {
        const targetWithOffset = getScene2TargetWithOffset()
        const isPortrait = window.innerWidth < window.innerHeight
        const isLandscape = !isPortrait
        

        
        // Dispatch camera-move event with orientation-appropriate target
        window.dispatchEvent(new CustomEvent('scene-camera-move', {
          detail: {
            targetPosition: getDefaultCameraPosition('scene2', isLandscape).position.clone(),
            targetLookAt: targetWithOffset,
            duration: ANIMATION_TIMINGS.orientationChange
          }
        }))
      }
    }

    window.addEventListener('resize', handleOrientationChange)
    window.addEventListener('orientationchange', handleOrientationChange)
    return () => {
      window.removeEventListener('resize', handleOrientationChange)
      window.removeEventListener('orientationchange', handleOrientationChange)
    }
  }, [hdriBlend])

  // Set initial target from shared default - only once when controls first exist
  useFrame(() => {
    if (controlsRef.current && !initialTargetSetRef.current) {
      const isPortrait = window.innerWidth < window.innerHeight
      const isLandscape = !isPortrait
      const t = getDefaultCameraPosition('scene1', isLandscape).target
      controlsRef.current.setTarget(t.x, t.y, t.z)
      initialTargetSetRef.current = true
    }
  })
  
  // Track zoom state reactively
  const [isZoomedIn, setIsZoomedIn] = useState(false)
  
  // Force disable controls in scene 2 when not zoomed in
  useEffect(() => {
    if (controlsRef.current) {
      const shouldBeEnabled = !isAnimating && !moveAnimate && (hdriBlend < 1 || isZoomedIn)
      controlsRef.current.enabled = shouldBeEnabled
    }
  }, [isAnimating, moveAnimate, hdriBlend, isZoomedIn])
  
  // Apply POI-specific camera limits with a delay after arrival
  useEffect(() => {
    if (isAtPOI && activePOI) {
      const timer = setTimeout(() => {
        setIsZoomedIn(true)
        
        // Determine which scene we're in and apply appropriate POI limits
        if (controlsRef.current) {
          let limits = null
          
          if (hdriBlend >= 1) {
            // Scene 2 - Black Arrow POI limits
            limits = poiLimits[activePOI]
          } else if (hdriBlend < 1) {
            // Scene 1 - Launch Platform POI limits
            limits = LAUNCH_PLATFORM_POI_LIMITS[activePOI]
          }
          
          if (limits) {
            controlsRef.current.minPolarAngle = limits.minPolarAngle
            controlsRef.current.maxPolarAngle = limits.maxPolarAngle
            controlsRef.current.minAzimuthAngle = limits.minAzimuthAngle
            controlsRef.current.maxAzimuthAngle = limits.maxAzimuthAngle
          }
        }
      }, ANIMATION_TIMINGS.poiZoomDelay)
      
      return () => clearTimeout(timer)
    } else {
      // Reset to default limits when exiting POI
      if (isZoomedIn && controlsRef.current) {
        setIsZoomedIn(false)
        
        // Apply default limits based on current scene
        let defaultLimits = null
        if (hdriBlend >= 1) {
          // Scene 2 defaults
          defaultLimits = CAMERA_CONTROLS_DEFAULTS.scene2Default
        } else {
          // Scene 1 defaults
          defaultLimits = CAMERA_CONTROLS_DEFAULTS.scene1
        }
        
        if (defaultLimits) {
          controlsRef.current.minPolarAngle = defaultLimits.minPolarAngle
          controlsRef.current.maxPolarAngle = defaultLimits.maxPolarAngle
          controlsRef.current.minAzimuthAngle = defaultLimits.minAzimuthAngle
          controlsRef.current.maxAzimuthAngle = defaultLimits.maxAzimuthAngle
        }
      }
    }
  }, [isAtPOI, activePOI, hdriBlend, isZoomedIn])
  
  // Update camera FOV based on device zoom level (from app.jsx)
  // For PerspectiveCamera: lower FOV = more zoomed in
  // cameraZoom: 400 (portrait) to 2000 (landscape)
  // Convert zoom to FOV: FOV = 75 / (zoom / 2000)
  useEffect(() => {
    if (camera1Ref.current && camera2Ref.current && camera3Ref.current) {
      // Calculate FOV based on zoom value
      // Base FOV at zoom 2000 is 24 degrees
      // As zoom decreases (portrait mode), increase FOV to show less (zoom in effect)
      const fovMultiplier = 2000 / cameraZoom
      const newFov = 24 * fovMultiplier
      
      camera1Ref.current.fov = newFov
      camera1Ref.current.updateProjectionMatrix()
      
      camera2Ref.current.fov = newFov
      camera2Ref.current.updateProjectionMatrix()
      
      camera3Ref.current.fov = newFov
      camera3Ref.current.updateProjectionMatrix()
    }
  }, [cameraZoom])
  
  // Swap pitch/yaw controls in landscape mode
  const controlsRefWithSwap = useCallback((node) => {
    controlsRef.current = node
  }, [])

  // Sync camera movements from camera1 to camera2
  useFrame(() => {
    if (!camera1Ref.current || !camera2Ref.current) return
    
    // Sync camera2 and camera3 with camera1 (which is controlled by OrbitControls)
    camera2Ref.current.position.copy(camera1Ref.current.position)
    camera2Ref.current.rotation.copy(camera1Ref.current.rotation)
    camera2Ref.current.quaternion.copy(camera1Ref.current.quaternion)
    
    camera3Ref.current.position.copy(camera1Ref.current.position)
    camera3Ref.current.rotation.copy(camera1Ref.current.rotation)
    camera3Ref.current.quaternion.copy(camera1Ref.current.quaternion)
    
    // Apply camera roll (dutch angle) if cameraRotation is provided
    if (cameraRotation !== 0) {
      // Create a quaternion for the roll around Z-axis (in camera's local space)
      const rollQuat = new THREE.Quaternion()
      rollQuat.setFromAxisAngle(new THREE.Vector3(0, 0, 1), cameraRotation)
      // Apply the roll after all other rotations
      camera1Ref.current.quaternion.multiplyQuaternions(camera1Ref.current.quaternion, rollQuat)
      camera2Ref.current.quaternion.copy(camera1Ref.current.quaternion)
      camera3Ref.current.quaternion.copy(camera1Ref.current.quaternion)
    }
    
    // Update matrices
    camera1Ref.current.updateMatrixWorld()
    camera2Ref.current.updateMatrixWorld()
    camera3Ref.current.updateMatrixWorld()
    
    // Render scenes to their textures (conditionally)
    if (renderTarget1Ref.current && renderTarget2Ref.current && renderTarget3Ref.current) {
      const originalRenderTarget = gl.getRenderTarget()
      
      // Render scene 1 only if needed
      if (renderScene1) {
        gl.setRenderTarget(renderTarget1Ref.current)
        gl.clear()
        gl.render(scene1Ref.current, camera1Ref.current)
        renderTarget1Ref.current.texture.needsUpdate = true
      }
      
      // Render scene 2 only if needed
      if (renderScene2) {
        gl.setRenderTarget(renderTarget2Ref.current)
        gl.clear()
        gl.render(scene2Ref.current, camera2Ref.current)
        renderTarget2Ref.current.texture.needsUpdate = true
      }
      
      // Render scene 3 only if needed
      if (renderScene3) {
        gl.setRenderTarget(renderTarget3Ref.current)
        gl.clear()
        gl.render(scene3Ref.current, camera3Ref.current)
        renderTarget3Ref.current.texture.needsUpdate = true
      }
      
      // Restore original render target
      gl.setRenderTarget(originalRenderTarget)
    }
  })
  
  return (
    <>
      {/* Camera movement controller - handles smooth camera animations */}
      {moveAnimate && moveTarget && camera1Ref.current && controlsRef.current && (
        <CameraMovementController
          camera={camera1Ref.current}
          controls={controlsRef.current}
          targetPosition={moveTarget.position}
          targetLookAt={moveTarget.targetLookAt}
          animate={moveAnimate}
          duration={moveTarget.duration}
          onAnimationComplete={() => setMoveAnimate(false)}
        />
      )}
      
      {/* CameraControls for synchronized camera movement */}
      {camera1Ref.current && (
        <CameraControlsComponent
          ref={controlsRefWithSwap}
          camera={camera1Ref.current}
          domElement={domElement}
          minDistance={CAMERA_CONTROLS_DEFAULTS.minDistance}
          maxDistance={CAMERA_CONTROLS_DEFAULTS.maxDistance}
          enableZoom={CAMERA_CONTROLS_DEFAULTS.enableZoom}
          maxPolarAngle={hdriBlend >= 1 ? (isZoomedIn ? Math.PI - 0.01 : Math.PI - 0.01) : Math.PI / 2}
          minPolarAngle={hdriBlend >= 1 ? (isZoomedIn ? 0.01 : 0.01) : Math.PI / 2.15}
          maxAzimuthAngle={hdriBlend >= 1 ? (isZoomedIn ? Math.PI * 2 : Math.PI * 2) : Math.PI / 16}
          minAzimuthAngle={hdriBlend >= 1 ? (isZoomedIn ? -Math.PI * 2 : -Math.PI * 2) : -Math.PI / 32}
          enabled={!moveAnimate && !isAnimating && (hdriBlend < 1 || isZoomedIn)}
          smoothTime={CAMERA_CONTROLS_DEFAULTS.smoothTime}
          enablePan={CAMERA_CONTROLS_DEFAULTS.enablePan}
          mouseButtons={{
            left: 1,
            middle: 0,
            right: 0,
            wheel: 0,
          }}
          touches={{
            one: 1,
            two: 0,
            three: 0,
          }}
        />
      )}
      
      {/* Portal Scene 1: Launch Platform - conditionally mount to reduce draw calls */}
      {mountScene1 && createPortal(
        <>
          <color attach="background" args={['#000000']} />
          {scene1Content}
          {shouldBakeShadows && <BakeShadows />}
        </>,
        scene1Ref.current
      )}
      
      {/* Portal Scene 2: Rocket Only - always mounted and ready */}
      {createPortal(
        <>
          {scene2Content}
        </>,
        scene2Ref.current
      )}
      
      {/* Portal Scene 3: Prospero with Earth environment - always mounted and ready */}
      {createPortal(
        <>
          {scene3Content}
        </>,
        scene3Ref.current
      )}
    </>
  )
}
